import type { ValueOf } from '../utils/types';
import type { SystemStyleObject, Theme } from '@mui/system';

export {
  API_DATE_FORMAT,
  DATE_DISPLAY_FORMAT,
  DATE_TIME_DISPLAY_FORMAT,
  MONTH_YEAR_DISPLAY_FORMAT,
} from './dateFormats';

export const AUTH0_DOMAIN = process.env.VITE_AUTH0_DOMAIN || '';
export const AUTH0_CLIENT_ID = process.env.VITE_AUTH0_CLIENT_ID || '';
export const AUTH0_AUDIENCE = process.env.VITE_AUTH0_AUDIENCE || '';
export const API_ENDPOINT = `${process.env.VITE_API_URL}/api/v1`;
export const API_V2_ENDPOINT = `${process.env.VITE_API_URL}/api/v2`;
export const APP_URL = process.env.VITE_URL || '';
export const APP_ENV = process.env.VITE_ENV || '';
export const APP_ZENDESK_URL = process.env.VITE_ZENDESK_URL || '';

export const CT_TIMEZONE_AREA = 'America/Chicago';

export enum FilterAndSortType {
  Select = 'select',
  Radio = 'radio',
  Checkbox = 'checkbox',
  AutoComplete = 'autocomplete',
}

export enum AdminPageTabs {
  ClientTab = 0,
  PayorTab = 1,
  ProviderTab = 2,
  BFTab = 3,
  GoalBankTab = 4,
  CohortsTab = 5,
  AllClientsTab = 6,
  AllProvidersTab = 7,
  EmailReportsTab = 8,
  FeatureFlagsTab = 9,
}

export enum GenericSearchType {
  TextInput = 'TextInput',
  DateRange = 'DateRange',
  AutoComplete = 'AutoComplete',
}

export enum FilterAndSortMode {
  Sort = 'sort',
  Filter = 'filter',
}

export const QUESTIONNAIRE_STATUS = {
  STARTED: 'STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
} as const;

export enum BTSessionStatus {
  Started = 'STARTED',
  AwaitingReview = 'AWAITING REVIEW',
  AwaitingGuardianSignature = 'AWAITING GUARDIAN SIGNATURE',
  EditsRequested = 'EDITS REQUESTED',
  Completed = 'COMPLETED',
  AwaitingFinalization = 'AWAITING FINALIZATION',
}

export enum SupervisionNoteStatus {
  Started = 'STARTED',
  Editing = 'EDITING',
  Completed = 'COMPLETED',
}

export enum AssessmentNoteStatus {
  Started = 'STARTED',
  Completed = 'COMPLETED',
}

export enum ClinicalQAStatus {
  AwaitingReview = 'AWAITING_REVIEW',
  EditsRequested = 'EDITS_REQUESTED',
  Completed = 'COMPLETED',
}

export enum SupervisionType {
  Client = 'client',
  Bt = 'bt',
  Parent = 'parent',
  Assessment = 'assessment',
  Assessment2 = 'assessment2',
  GroupParent = 'groupParent',
}

export enum ActionRequiredItemEnum {
  Client = 'client',
  Bt = 'bt',
  Parent = 'parent',
  Assessment = 'assessment',
  Assessment2 = 'assessment2',
  GroupParent = 'groupParent',
  TreatmentPlan = 'treatmentPlan',
  Session = 'session',
}

export enum SessionNoteSteps {
  First,
  Second,
}

export enum Roles {
  Admin = 'admin',
  Prbt = 'prbt',
  Bcba = 'bcba',
  OnboardingCoordinator = 'onboarding_coordinator',
  QaSpecialist = 'clinical_documentation_qa_specialist',
  ClientSuccessManager = 'client_success_manager',
  Biller = 'biller',
  InvoiceCoordinator = 'invoice_coordinator',
  AssistantAssessor = 'assistant_assessor',
  Vbt = 'vbt',
  Guardian = 'guardian',
  LimitedAccessGuardian = 'limited_access_guardian',
}

export enum ClinicalRelationships {
  AssistantAssessor = 'assistant_assessor',
  HomeBcba = 'home_bcba',
  BobbingBcba = 'bobbing_bcba',
  SubstituteBCBA = 'substitute_bcba',
  Prbt = 'prbt',
  QaSpecialist = 'cqa',
  Vbt = 'vbt',
  PracticingGuardian = 'practicing_guardian',
  Parent = 'parent',
  LimitedAccessGuardian = 'limited_access_guardian',
}

export enum Themes {
  Primary = 'primary',
}

export enum ProgressBarThemes {
  Primary = 'primary',
  Secondary = 'secondary',
  Error = 'error',
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
}

export const defaultSegmentOne = {
  'Expressive/receptive communication deficits': false,
  'Social communication deficits': false,
  'Non-verbal communication deficits': false,
  'Social interaction deficits': false,
  'Social-emotional reciprocity deficits': false,
  'Adaptive skills deficits': false,
  'Play skills deficit': false,
  'Motor skills deficit': false,
  'Community/safety skills deficits': false,
  'Joint attention skills deficits': false,
  'School readiness skills deficits': false,
  'Cognitive skills deficits': false,
  'Restricted, repetitive behaviors': false,
  'Maladaptive behaviors': false,
  Other: '',
};

export const defaultSegmentTwo = {
  'Antecedent-Based Interventions (ABI)': false,
  'Cognitive Behavioral Intervention (CBI)': false,
  'Differential Reinforcement of Alternative, Incompatible, or Other Behaviors (DRA/I/O)': false,
  'Discrete Trial Teaching (DTT)': false,
  'Functional Communication Training (FCT)': false,
  'Modeling (MD)': false,
  'Naturalistic Intervention (NI)': false,
  'Parent-Implemented Intervention (PII)': false,
  'Peer-Mediated Instruction & Intervention (PMII)': false,
  'Picture Exchange Communication Systems (PECS)': false,
  'Pivotal Response Training (PRT)': false,
  'Prompting (PP)': false,
  'Reinforcement (R+)': false,
  Other: '',
};

export const defaultSessionNarrative = {
  'Client adheres with targets or goals': false,
  'No behavior issues or family changes': false,
  "Description of session and client's response": '',
};

export const HEADER_HEIGHT = 100;
export const SIDEBAR_WIDTH = 300;
export const NOTIFICATION_TIMER = 60000;

export enum BcbaAttendance {
  Present = 'Yes',
  Absent = 'No',
}

export const MAX_UTILIZATIONS = 2;

export enum SessionNoteSignatures {
  BtSignature = 'BT Signature',
  ParentSignature = 'Parent Signature',
}

export const STANDARD_ERROR_MESSAGES = {
  required: 'This field is required',
  invalidValue: 'Invalid value',
  tenDigits: '10 digits are required',
  twoChars: '2 characters are required',
  minFiveDigits: 'Must be at least 5 digits',
};

export const ENTITY_PREFIX = {
  Claim: 'FCL',
  ClientAssessmentNote: 'FA',
  ClientSupervisionNotes: 'FCSN',
  SessionNotes: 'F',
  TreatmentPlan: 'FT',
  ParentSupervisionNotes: 'FPSN',
  BTSupervisionNotes: 'FBSN',
  GroupSupervisionNotes: 'FGP',
  Invoice: 'FIN',
  Client: 'FC',
  OnboardingQuestionnaire: 'FOBD',
  IntakeQuestionnaire: 'FINT',
} as const;

export const EMAIL_REGEX = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;

export const IPAD_AIR_VIEWPORT_WIDTH = 1180;
export const MAX_WARNING_POPPER_VISIBLE_LENGTH = 20;

export const MAX_TIMESHEET_DURATION_HOURS = 3;

export const US_STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama (AL)' },
  { value: 'AK', label: 'Alaska (AK)' },
  { value: 'AZ', label: 'Arizona (AZ)' },
  { value: 'AR', label: 'Arkansas (AR)' },
  { value: 'CA', label: 'California (CA)' },
  { value: 'CO', label: 'Colorado (CO)' },
  { value: 'CT', label: 'Connecticut (CT)' },
  { value: 'DE', label: 'Delaware (DE)' },
  { value: 'DC', label: 'District of Columbia (DC)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'GA', label: 'Georgia (GA)' },
  { value: 'GU', label: 'Guam (GU)' },
  { value: 'HI', label: 'Hawaii (HI)' },
  { value: 'ID', label: 'Idaho (ID)' },
  { value: 'IL', label: 'Illinois (IL)' },
  { value: 'IN', label: 'Indiana (IN)' },
  { value: 'IA', label: 'Iowa (IA)' },
  { value: 'KS', label: 'Kansas (KS)' },
  { value: 'KY', label: 'Kentucky (KY)' },
  { value: 'LA', label: 'Louisiana (LA)' },
  { value: 'ME', label: 'Maine (ME)' },
  { value: 'MD', label: 'Maryland (MD)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'MI', label: 'Michigan (MI)' },
  { value: 'MN', label: 'Minnesota (MN)' },
  { value: 'MS', label: 'Mississippi (MS)' },
  { value: 'MO', label: 'Missouri (MO)' },
  { value: 'MT', label: 'Montana (MT)' },
  { value: 'NE', label: 'Nebraska (NE)' },
  { value: 'NV', label: 'Nevada (NV)' },
  { value: 'NH', label: 'New Hampshire (NH)' },
  { value: 'NJ', label: 'New Jersey (NJ)' },
  { value: 'NM', label: 'New Mexico (NM)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'ND', label: 'North Dakota (ND)' },
  { value: 'OH', label: 'Ohio (OH)' },
  { value: 'OK', label: 'Oklahoma (OK)' },
  { value: 'OR', label: 'Oregon (OR)' },
  { value: 'PA', label: 'Pennsylvania (PA)' },
  { value: 'PR', label: 'Puerto Rico (PR)' },
  { value: 'RI', label: 'Rhode Island (RI)' },
  { value: 'SC', label: 'South Carolina (SC)' },
  { value: 'SD', label: 'South Dakota (SD)' },
  { value: 'TN', label: 'Tennessee (TN)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'UT', label: 'Utah (UT)' },
  { value: 'VT', label: 'Vermont (VT)' },
  { value: 'VI', label: 'Virgin Islands (VI)' },
  { value: 'VA', label: 'Virginia (VA)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'WV', label: 'West Virginia (WV)' },
  { value: 'WI', label: 'Wisconsin (WI)' },
  { value: 'WY', label: 'Wyoming (WY)' },
] as const;

export const MINIMUM_REQUIRED_SENTENCE_LENGTH = 60;
export const MAXIMUM_TARGET_INSTRUCTION_LENGTH = 2000;
export const SUPERVISION_NOTES_INPUT_MIN_LENGTH = 5;

export const usStates = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const DEFAULT_INPUT_HEIGHT = 40;

export enum BackgroundTypes {
  White = 'white',
  Colored = 'colored',
  EvenColored = 'even-colored',
}

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

export enum TargetType {
  SuccessFailure,
  Instances,
  InstancesWithDuration,
  Rate,
  Duration,
}

export const numberInputStyleWithoutArrow: SystemStyleObject<Theme> = {
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    padding: '0.5em',
  },
};

export const selectedDateForQAWeekly =
  process.env.VITE_TEST === 'true'
    ? new Date(2024, 1, 11)
    : new Date(new Date().setMilliseconds(0));

export enum FacilityAssignmentType {
  BILLING = 'billing',
  SERVICE = 'service',
}

export enum FacilityAssignmentTypeLabel {
  BILLING = 'Billing Facility',
  SERVICE = 'Service Location',
}

export enum AssignmentStatus {
  ASSIGNED = 'Assigned',
  UNASSIGNED = 'Unassigned',
  PENDING = 'Pending',
}

export enum PayorResponsibilityEnum {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  TERTIARY = 'TERTIARY',
}

export const clinicalRelationshipOptions = [
  { value: ClinicalRelationships.AssistantAssessor, label: 'Assistant Assessor' },
  { value: ClinicalRelationships.Prbt, label: 'pBT' },
  { value: ClinicalRelationships.HomeBcba, label: 'Home BCBA' },
  { value: ClinicalRelationships.BobbingBcba, label: 'Bobbing BCBA' },
  { value: ClinicalRelationships.SubstituteBCBA, label: 'Substitute BCBA' },
  { value: ClinicalRelationships.PracticingGuardian, label: 'Practicing Guardian' },
  { value: ClinicalRelationships.Vbt, label: 'BT' },
  { value: ClinicalRelationships.Parent, label: 'Parent' },
  { value: ClinicalRelationships.LimitedAccessGuardian, label: 'Limited Access Guardian' },
];

export enum PriorAuthorizationNumberEnum {
  prior_authorization_1 = 'Prior Authorization 1',
  prior_authorization_2 = 'Prior Authorization 2',
}

export enum CohortAssignmentMode {
  Client = 'client',
  Cohort = 'cohort',
}

export const INSURANCE_TYPES = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
} as const;

export const TREATMENT_PLAN_BASE_PATH = '/treatment-plan';
export const TREATMENT_PLAN_V3_VERSION = 3;

export const BAYS = {
  BAY_2_5: 'BAY 2.5',
  BAY_2_6: 'BAY 2.6',
  BAY_3: 'BAY 3',
  BAY_6: 'BAY 6',
  BAY_7: 'BAY 7',
} as const;

export enum InvoiceStatus {
  PAID = 'Paid',
  NOT_PAID = 'Not paid',
  PARTIALLY_PAID = 'Partial payment',
  IN_PROGRESS = 'In progress',
}

export enum InvoicePaymentStatus {
  PENDING = 'Pending',
  PROCESSING = 'Processing',
  SUCCESS = 'Success',
  FAILED = 'Failed',
  CANCELLED = 'cancelled',
}

export enum ServiceCodeTypes {
  Cpt = 'CPT',
  Hcpcs = 'HCPCS',
}
export const HcpcsServiceCodeMapping = {
  '97151': 'H0031',
  '97152': 'H0032',
  '97153': 'H2019',
  '97155': 'H2012',
  '97156': 'S5110',
  '97157': 'S5111',
  '99366': '99366',
};

export const TIME_RANGE_FIELDS = {
  DATE_START: 'date_start',
  EXPECTED_DURATION: 'expected_duration',
} as const;

export type TimeRangeField = ValueOf<typeof TIME_RANGE_FIELDS>;
